import React from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faListMusic, faPlus } from '@soundxyz/font-awesome/pro-regular-svg-icons/index.mjs';
import { faCheck } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { gql } from '@soundxyz/gql-string';
import { styled, theme } from '@components-shared/src/stitches';
import { CoverImages } from '@components/Playlists/basics/CoverImages';
import { AccessibleLink } from '@components/main-components/link/AccessibleLink';
import type { FragmentType } from '@graphql/reactQuery';
import { getFragment } from '@graphql/reactQuery';
import { AddToPlaylistRowFragmentDoc } from '@graphql/reactQuery';
import { formatPlayDuration } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment AddToPlaylistRow on Shelf {
    id
    name
    playTimeInSeconds
    releaseCount
    ...PlaylistCoverImages
  }
`);

export const PlaylistRow = ({
  isSelected,
  isAlreadyAdded,
  onSelect,
  playlistFragment,
}: {
  isSelected: boolean;
  isAlreadyAdded: boolean;
  onSelect: () => void;
  playlistFragment: FragmentType<AddToPlaylistRowFragmentDoc>;
}) => {
  const playlist = getFragment(AddToPlaylistRowFragmentDoc, playlistFragment);

  const duration = formatPlayDuration({
    playTimeInSeconds: playlist.playTimeInSeconds,
  });

  const isAdded = isSelected || isAlreadyAdded;

  return (
    <Row>
      <ImageContainer>
        <CoverImages playlist={playlist} maxSize={48} priority={false} />
      </ImageContainer>
      <PlaylistInfoContainer>
        <PlaylistTitle>{playlist.name}</PlaylistTitle>
        <PlaylistInfo>
          {playlist.releaseCount} Sound{playlist.releaseCount == 1 ? '' : 's'}
          {duration ? ` • ${duration}` : ''}
        </PlaylistInfo>
      </PlaylistInfoContainer>
      {isAlreadyAdded && <AlreadyAddedText>Added</AlreadyAddedText>}
      <AddButton onClick={onSelect} isDisabled={isAlreadyAdded} isAdded={isAdded}>
        {isAdded ? (
          <FontAwesomeIcon icon={faCheck} color={theme.colors.white.toString()} size="lg" />
        ) : (
          <FontAwesomeIcon icon={faPlus} color={theme.colors.neutral800.toString()} size="lg" />
        )}
      </AddButton>
    </Row>
  );
};

export const NewPlaylistRow = ({ releaseId }: { releaseId: string }) => (
  <AccessibleLink pathnameUri={`/playlist/new?initialRelease=${releaseId}`}>
    <Row>
      <ImageContainer newPlaylist>
        <FontAwesomeIcon icon={faListMusic} size="lg" />
      </ImageContainer>
      <PlaylistInfoContainer>
        <PlaylistTitle>Create a new playlist</PlaylistTitle>
      </PlaylistInfoContainer>
    </Row>
  </AccessibleLink>
);

const AlreadyAddedText = styled('span', {
  base: 'S',
  color: '$neutral500',
  marginRight: 6,

  '@tablet': {
    display: 'none',
  },
});

const Row = styled('div', {
  display: 'flex',
  height: 81,
  color: '$neutral600',
  fontSize: 13,
  lineHeight: '18px',
  alignItems: 'center',
  borderBottom: '1px solid $neutral200',
  width: '100%',

  [`&:hover ${AlreadyAddedText}`]: {
    display: 'inline',
  },
});

const AddButton = styled('button', {
  borderRadius: 6,
  width: 28,
  height: 28,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: 8,

  variants: {
    isAdded: {
      true: {
        backgroundColor: '$neutral900',
        svg: { width: 16 },
      },

      false: {
        backgroundColor: '$white',
        border: '1px solid $neutral500',
        svg: { path: { stroke: '$neutral500' } },
        '&:hover': {
          svg: { path: { stroke: '$neutral900' } },
          borderColor: '$neutral900',
        },
      },
    },

    // used in compound variant below
    isDisabled: { true: {} },
  },

  compoundVariants: [
    {
      isAdded: true,
      isDisabled: true,
      css: {
        opacity: 0.2,
        cursor: 'not-allowed',
      },
    },
  ],
});

const PlaylistTitle = styled('span', {
  base: 'L',
  fontStyle: 'semibold',
  display: 'block',
});

const PlaylistInfo = styled('span', {
  base: 'S',
  color: '$neutral700',
  marginTop: 8,
});

const PlaylistInfoContainer = styled('div', {
  marginRight: 'auto',
});

const ImageContainer = styled('div', {
  overflow: 'hidden',
  position: 'relative',
  height: 48,
  width: 48,
  borderRadius: 8,
  marginRight: 16,

  variants: {
    newPlaylist: {
      true: {
        backgroundColor: 'black',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '$white',
      },
    },
  },
});
